import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import {
  REQUEST_LIST_API,
  REQUEST_LIST_COUNT_API,
} from '../../../constants/api'
import { EMPTY_OBJECT } from '../../../constants/common'
import { REQUEST_ENTITY } from '../../../constants/entity'
import {
  SUBMENU_DEFAULT_VALUE,
  SUBMENU_PARAM_KEY,
} from '../../../constants/route'
import { ScreenContainerRef } from '../../../context'
import {
  useQueryParamSubTab,
  useRegisterReload,
  useReloadCountersAfterRefetch,
} from '../../../hooks'
import { getSubSearchParam, isSubArchive } from '../../../utils/route'
import { PageLoader } from '../../Page'
import { getEmptyStateActions, PageEmptyState } from '../../PageEmptyState'
import {
  getMassActions,
  RequestTableLayout,
  useRequestTablePage,
} from '../../Request'
import { TablePagination } from '../../TablePagination'

import {
  ENTITY_TAB_REQUESTS_DEFAULT_QA_NAME,
  ENTITY_TAB_REQUESTS_SUB_TAB_ORDER,
} from './EntityTabRequests.constants'
import { useEntityTabRequestsCreation } from './EntityTabRequests.hooks'
import {
  getEntityTabRequestsExtraFilters,
  hasEntityTabRequestsItems,
  hasEntityTabRequestsVisibleFilters,
  isEntityTabRequestsToolbarCollapsed,
} from './EntityTabRequests.utils'
import { EntityTabRequestsSubTabs } from './EntityTabRequestsSubTabs'
import { EntityTabRequestsToolbar } from './EntityTabRequestsToolbar'

import * as styles from './EntityTabRequests.module.css'

import ServiceRequestIcon from '../../../icons/service-request.svg'

function EntityTabRequests({
  canViewArchive,
  countApi = REQUEST_LIST_COUNT_API,
  counters = EMPTY_OBJECT,
  creationBuildingApiParams,
  creationInitialBuilding,
  creationInitialFlat,
  creationInitialUser,
  creationInitialValues,
  emptyStateTextKey = 'Request.NoDataKey',
  entity = REQUEST_ENTITY,
  filterStateApi,
  listApi = REQUEST_LIST_API,
  qaName = ENTITY_TAB_REQUESTS_DEFAULT_QA_NAME,
  scopeFilterKey,
  scopeId,
  shouldShowCreateAction = true,
  onApplyTableConfig,
  onRegisterReload,
  onReloadCounters,
  onReloadOwnCounters,
}) {
  const { t } = useTranslation()

  const [modal, setModal] = useState(null)

  function handleCloseModal() {
    setModal(null)
  }

  const subTabOrder = canViewArchive
    ? ENTITY_TAB_REQUESTS_SUB_TAB_ORDER
    : [SUBMENU_DEFAULT_VALUE]

  const { activeSubTab, setActiveSubTab } = useQueryParamSubTab({
    allowedValues: subTabOrder,
    paramKey: SUBMENU_PARAM_KEY,
    readValue: getSubSearchParam,
  })

  const isArchiveSubTab = isSubArchive(activeSubTab)

  const asyncTableOptions = {
    countApi,
    extraFilters: getEntityTabRequestsExtraFilters({
      activeSubTab,
      scopeFilterKey,
      scopeId,
    }),
    filterStateApi,
    listApi,
    withoutSubMenu: true,
  }

  const tablePage = useRequestTablePage({
    asyncTableOptions,
    entity,
    extraSelectionResetDeps: [activeSubTab, scopeId],
    setModal,
    sub: SUBMENU_DEFAULT_VALUE,
    withoutBrowserTabTitle: true,
    onApplyTableConfig,
    onCloseModal: handleCloseModal,
  })

  const {
    availableFilters,
    canCreateDocFlow,
    filterValues,
    handleChangeShouldReloadFilters,
    handleLoadInit,
    handleReload,
    handleSetFilterValues,
    handleSetPagination,
    hasFilterChanged,
    isInitialLoadSettled,
    isLoading,
    isPerformingMassAction,
    isRefetching,
    items: requests,
    ordering,
    pageScrollWrapperRef,
    pagination,
    permissions,
    selectAllPages,
    selected,
    shouldReloadFilters,
  } = tablePage

  const { handleReloadWithoutCounters } = useReloadCountersAfterRefetch({
    isRefetching,
    reload: handleReload,
    onReloadCounters,
    onReloadOwnCounters,
  })

  // Expose the table reload to the profile so out-of-tab openers (e.g. the
  // actions widget "Add new service request") can refresh a visible list.
  useRegisterReload({
    isReady: isInitialLoadSettled,
    reload: handleReloadWithoutCounters,
    onRegisterReload,
  })

  useEffect(() => {
    if (scopeId) {
      handleLoadInit()
    }
  }, [scopeId])

  const { handleAddAction, isWizardEnabled, requestCreationModal } =
    useEntityTabRequestsCreation({
      creationBuildingApiParams,
      creationInitialBuilding,
      creationInitialFlat,
      creationInitialUser,
      creationInitialValues,
      onClose: handleCloseModal,
      onReload: handleReload,
    })

  const hasRequests = hasEntityTabRequestsItems(requests)

  const showEmptyState = !hasRequests && !isLoading

  const shouldShowCreate = shouldShowCreateAction && permissions?.can_create

  const shouldShowCreateButton = shouldShowCreate && !isArchiveSubTab

  const shouldShowEmptyStateActions =
    shouldShowCreateButton && !hasFilterChanged

  const emptyStateActions = getEmptyStateActions({
    actionName: qaName.addNew,
    isLoading,
    qaName: qaName.addNew,
    shouldShowActions: shouldShowEmptyStateActions,
    text: t('RequestTable.AddNewBtnKey'),
    onClick: handleAddAction,
  })

  const hasVisibleFilters = hasEntityTabRequestsVisibleFilters(availableFilters)

  const shouldCollapseToolbar = isEntityTabRequestsToolbarCollapsed({
    hasFilterChanged,
    hasRequests,
    hasVisibleFilters,
    isLoading,
    shouldShowCreateButton,
  })

  // `useRequestTablePage` derives mass actions from the fixed `sub`, so on the
  // Archive sub-tab they'd stay the "all" set (edit actions, no restore).
  // Recompute them from `activeSubTab` — same approach as the related-requests
  // tab, which keeps `sub` fixed and drives archive actions explicitly.
  const massActions = getMassActions({
    canCreateDocFlow,
    permissions,
    selectAllPages,
    selectCount: selected.length,
    sub: activeSubTab,
    t,
  })

  return (
    <ScreenContainerRef value={pageScrollWrapperRef}>
      <div className={styles.EntityTabRequests} ref={pageScrollWrapperRef}>
        {isPerformingMassAction || isRefetching ? <PageLoader /> : null}
        {subTabOrder.length > 1 ? (
          <EntityTabRequestsSubTabs
            activeSubTab={activeSubTab}
            counters={counters}
            subTabOrder={subTabOrder}
            onSetActiveSubTab={setActiveSubTab}
          />
        ) : null}
        {shouldCollapseToolbar ? null : (
          <EntityTabRequestsToolbar
            activeSubTab={activeSubTab}
            availableFilters={availableFilters}
            entity={entity}
            filterValues={filterValues}
            hasVisibleFilters={hasVisibleFilters}
            isLoading={isLoading}
            isWizardEnabled={isWizardEnabled}
            ordering={ordering}
            pagination={pagination}
            qaName={qaName}
            scopeFilterKey={scopeFilterKey}
            scopeId={scopeId}
            shouldReloadFilters={shouldReloadFilters}
            shouldShowCreateButton={shouldShowCreateButton}
            onAddAction={handleAddAction}
            onChangeShouldReloadFilters={handleChangeShouldReloadFilters}
            onReload={handleReload}
            onSetFilterValues={handleSetFilterValues}
          />
        )}
        <div
          className={classNames(styles.content, {
            [styles.contentEmpty]: showEmptyState,
          })}
        >
          <RequestTableLayout
            emptyState={
              <PageEmptyState
                EntityIcon={ServiceRequestIcon}
                actions={emptyStateActions}
                text={t(emptyStateTextKey)}
              />
            }
            entity={entity}
            extraModals={requestCreationModal}
            hasTable={!showEmptyState}
            modal={modal}
            tablePage={{ ...tablePage, massActions }}
          />
        </div>
        <TablePagination
          responsive
          isSkeleton={isLoading}
          value={pagination}
          onChange={handleSetPagination}
        />
      </div>
    </ScreenContainerRef>
  )
}

export default EntityTabRequests
